import { Box, Stack, Typography } from '@mui/material';
import { AttendanceCode, AttendanceRegisterStatisticsResponse } from '@schooly/api';
import { EmptyFolderSvg } from '@schooly/style';
import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { aggregatePeriodTotals, aggregateStatisticsByStructure } from '../attendance-utils';
import { AttendanceStats } from '../AttendanceStatistics/AttendanceStats';
import { AttendanceRegisterPreviewStructureGrid } from './AttendanceRegisterPreviewStructureGrid';

interface AttendanceRegisterPreviewPeriodsTabProps {
  register: AttendanceRegisterStatisticsResponse;
  codes: AttendanceCode[];
}

export const AttendanceRegisterPreviewPeriodsTab: FC<AttendanceRegisterPreviewPeriodsTabProps> = ({
  register,
  codes,
}) => {
  const { formatMessage } = useIntl();

  const { totalCounts, structures } = useMemo(
    () => ({
      totalCounts: aggregatePeriodTotals(register),
      structures: aggregateStatisticsByStructure(register),
    }),
    [register],
  );

  if (!structures.length) {
    return (
      <Stack flex={1}>
        <PeriodsEmptyStub />
      </Stack>
    );
  }

  return (
    <Stack overflow="hidden" gap={2.5}>
      <AttendanceStats
        statistics={[{ counts: totalCounts, name: formatMessage({ id: 'attendance-Period' }) }]}
        showJustifications={false}
        showChartBars={false}
        sx={{ '& .MuiCard-root': { border: 'none', flex: 1 } }}
      />

      <Stack gap={2.5} overflow="auto">
        {structures.map((structure) => (
          <AttendanceRegisterPreviewStructureGrid
            key={structure.id}
            codes={codes}
            structure={structure}
          />
        ))}
      </Stack>
    </Stack>
  );
};

const PeriodsEmptyStub: FC = () => {
  const { formatMessage } = useIntl();

  return (
    <Stack gap={2.5} justifyContent="center" alignItems="center" flex={1}>
      <Box sx={{ svg: { width: 300 } }}>
        <EmptyFolderSvg />
      </Box>
      <Typography variant="h3" textAlign="center" color="text.primary">
        {formatMessage({ id: 'attendance-NoPeriodAttendance' })}
      </Typography>
    </Stack>
  );
};
